import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import "./Korpa.scss";
import { useCart } from "../../shared/CartContext";
import { createOrder, decrementStock } from "../../shop/api/orders";
import { getZones, getAvailableHours } from "../../shop/api/delivery";
import { supabase } from "../../shop/api/supabase";

const formatPrice = (num) => num.toLocaleString("sr-RS") + " RSD";

const formatHour = (h) => `${String(h).padStart(2, "0")}:00–${String(h + 1).padStart(2, "0")}:00h`;

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("sr-Latn-RS", { weekday: "long", day: "numeric", month: "long" });

function getAvailableDates() {
  const dates = [];
  const today = new Date();
  for (let i = 0; i < 14; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    dates.push(d.toISOString().split("T")[0]);
  }
  return dates;
}

export default function Korpa() {
  const { items, removeFromCart, updateQty, clearCart, totalPrice } = useCart();
  const [porudzbina, setPorudzbina] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ ime: "", email: "", telefon: "", adresa: "", napomena: "" });

  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedOpstina, setSelectedOpstina] = useState("");

  const [selectedDate, setSelectedDate] = useState("");
  const [availableHours, setAvailableHours] = useState([]);
  const [selectedHour, setSelectedHour] = useState(null);
  const [loadingHours, setLoadingHours] = useState(false);

  const availableDates = getAvailableDates();

  useEffect(() => {
    getZones().then(setZones).catch(console.error);
  }, []);

  const handleOpstina = (opstina) => {
    setSelectedOpstina(opstina);
    setSelectedDate("");
    setSelectedHour(null);
    setAvailableHours([]);
    const zone = zones.find((z) => z.opstine.includes(opstina));
    setSelectedZone(zone || null);
  };

  const handleDate = async (date) => {
    setSelectedDate(date);
    setSelectedHour(null);
    setAvailableHours([]);
    if (!selectedZone || !date) return;
    setLoadingHours(true);
    const hours = await getAvailableHours(selectedZone.id, date);
    setAvailableHours(hours);
    setLoadingHours(false);
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedOpstina) { setError("Molimo izaberite opštinu."); return; }
    if (!selectedDate) { setError("Molimo izaberite datum dostave."); return; }
    if (availableHours.length > 0 && selectedHour === null) { setError("Molimo izaberite termin dostave."); return; }

    setSubmitting(true);
    setError(null);

    try {
      for (const item of items) {
        const { data: prod } = await supabase
          .from("products")
          .select("kolicina_na_stanju")
          .eq("slug", item.slug)
          .single();
        if (!prod || prod.kolicina_na_stanju < item.kolicina) {
          setError(`"${item.title}" nije dostupno u traženom broju komada.`);
          setSubmitting(false);
          return;
        }
      }

      const orderItems = items.map((item) => ({
        slug: item.slug, title: item.title, cena: item.cena,
        kolicina: item.kolicina, pic: item.pic,
      }));

      const order = await createOrder({
        customer: { ...form, opstina: selectedOpstina },
        items: orderItems,
        total: totalPrice,
        opstina: selectedOpstina,
        delivery_date: selectedDate,
        delivery_hour: selectedHour,
        delivery_zone_id: selectedZone?.id || null,
      });

      for (const item of items) {
        const { data: prod } = await supabase.from("products").select("id").eq("slug", item.slug).single();
        if (prod) await decrementStock(prod.id, item.kolicina);
      }

      await supabase.functions.invoke("send-order-email", { body: { order } });

      clearCart();
      setPorudzbina(true);
    } catch (err) {
      console.error(err);
      setError("Došlo je do greške. Pokušajte ponovo.");
    } finally {
      setSubmitting(false);
    }
  };

  if (porudzbina) {
    return (
      <div className="korpa">
        <div className="korpa__success">
          <div className="korpa__success-icon">✝</div>
          <h2>Hvala na porudžbini!</h2>
          <p>Kontaktiraćemo Vas na navedenu email adresu u najkraćem roku.</p>
          <p style={{ fontSize: "0.85rem", color: "#5a3c1e" }}>Da biste osigurali prijem emaila, dodajte <strong>info@ikone.rs</strong> u svoje kontakte.</p>
          <NavLink to="/prodavnica" className="korpa__success-btn">Nastavi kupovinu</NavLink>
        </div>
      </div>
    );
  }

  const allOpstine = zones.flatMap((z) => z.opstine).sort();

  return (
    <div className="korpa">
      <div className="korpa__header">
        <div className="korpa__header-inner">
          <h1 className="korpa__title">Korpa</h1>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="korpa__empty">
          <span className="korpa__empty-icon">🛍</span>
          <p>Vaša korpa je prazna.</p>
          <NavLink to="/prodavnica" className="korpa__empty-btn">Pogledaj ikone</NavLink>
        </div>
      ) : (
        <div className="korpa__body">

          <div className="korpa__items">
            <div className="korpa__items-header">
              <span>Ikona</span>
              <span>Cena</span>
              <span>Količina</span>
              <span>Ukupno</span>
              <span></span>
            </div>

            {items.map((item) => {
              const cenaNum = parseFloat(item.cena.replace(/[^\d,]/g, "").replace(",", ".")) || 0;
              return (
                <div key={item.slug} className="korpa__item">
                  <div className="korpa__item-product">
                    <div className="korpa__item-img">
                      <img src={item.pic} alt={item.title} />
                    </div>
                    <div className="korpa__item-details">
                      <NavLink to={`/prodavnica/${item.slug}`} className="korpa__item-title">{item.title}</NavLink>
                      <span className="korpa__item-meta">{item.tehnika} · {item.dimenzije}</span>
                      <span className="korpa__item-sifra">Šifra: {item.sifra}</span>
                    </div>
                  </div>
                  <div className="korpa__item-price">{item.cena}</div>
                  <div className="korpa__item-qty">
                    <button onClick={() => updateQty(item.slug, item.kolicina - 1)}>−</button>
                    <span>{item.kolicina}</span>
                    <button onClick={() => updateQty(item.slug, item.kolicina + 1)}>+</button>
                  </div>
                  <div className="korpa__item-total">{formatPrice(cenaNum * item.kolicina)}</div>
                  <button className="korpa__item-remove" onClick={() => removeFromCart(item.slug)} aria-label="Ukloni">
                    <i className="fa-solid fa-trash" />
                  </button>
                </div>
              );
            })}

            <div className="korpa__items-footer">
              <button className="korpa__clear-btn" onClick={clearCart}>Isprazni korpu</button>
              <NavLink to="/prodavnica" className="korpa__continue-btn">← Nastavi kupovinu</NavLink>
            </div>
          </div>

          <div className="korpa__sidebar">
            <div className="korpa__summary">
              <h3 className="korpa__summary-title">Pregled porudžbine</h3>
              {items.map((item) => {
                const cenaNum = parseFloat(item.cena.replace(/[^\d,]/g, "").replace(",", ".")) || 0;
                return (
                  <div key={item.slug} className="korpa__summary-row">
                    <span>{item.title} × {item.kolicina}</span>
                    <span>{formatPrice(cenaNum * item.kolicina)}</span>
                  </div>
                );
              })}
              <div className="korpa__summary-divider" />
              <div className="korpa__summary-total">
                <span>Ukupno</span>
                <strong>{formatPrice(totalPrice)}</strong>
              </div>
              <p className="korpa__summary-note">* Plaćanje pouzećem ili uplatnicom.</p>
            </div>

            <form className="korpa__form" onSubmit={handleSubmit}>
              <h3 className="korpa__form-title">Podaci za dostavu</h3>

              <div className="korpa__field">
                <label>Ime i prezime *</label>
                <input name="ime" value={form.ime} onChange={handleChange} required />
              </div>
              <div className="korpa__field">
                <label>Email adresa *</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} required />
              </div>
              <div className="korpa__field">
                <label>Telefon *</label>
                <input name="telefon" value={form.telefon} onChange={handleChange} required />
              </div>
              <div className="korpa__field">
                <label>Adresa dostave *</label>
                <input name="adresa" value={form.adresa} onChange={handleChange} required placeholder="Ulica i broj" />
              </div>

              <div className="korpa__field">
                <label>Opština *</label>
                <select value={selectedOpstina} onChange={(e) => handleOpstina(e.target.value)} required>
                  <option value="">— Izaberite opštinu —</option>
                  {allOpstine.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>

              {selectedOpstina && (
                <div className="korpa__field">
                  <label>Datum dostave *</label>
                  <select value={selectedDate} onChange={(e) => handleDate(e.target.value)} required>
                    <option value="">— Izaberite datum —</option>
                    {availableDates.map((d) => (
                      <option key={d} value={d}>{formatDate(d)}</option>
                    ))}
                  </select>
                </div>
              )}

              {selectedDate && (
                <div className="korpa__field">
                  <label>Termin dostave</label>
                  {loadingHours ? (
                    <p className="korpa__slots-loading">Učitavanje termina...</p>
                  ) : availableHours.length === 0 ? (
                    <p className="korpa__slots-empty">Nema slobodnih termina za izabrani datum. Pokušajte drugi dan.</p>
                  ) : (
                    <select
                      value={selectedHour ?? ""}
                      onChange={(e) => setSelectedHour(e.target.value ? parseInt(e.target.value) : null)}
                    >
                      <option value="">— Izaberite termin —</option>
                      {availableHours.map((h) => (
                        <option key={h} value={h}>{formatHour(h)}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              <div className="korpa__field">
                <label>Napomena</label>
                <textarea name="napomena" value={form.napomena} onChange={handleChange} rows={3} />
              </div>

              {error && <p className="korpa__error">{error}</p>}

              <button type="submit" className="korpa__submit" disabled={submitting}>
                {submitting ? "Slanje..." : "Poruči"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
